import React, { useState } from "react";
import styled from "styled-components";
import { shorten } from "@backpacker/number";
import { content } from "@glossy";
import Button from "@ui-kit/button";
import PopoverButton from "@ui-kit/popover/button";
import SafeHTML from "@ui-kit/safe-html";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { usePinnedPostWidget } from "@radarly/core/widgets/use-cases";
import { tr } from "@radarly/i18n";
import { PinnedPostCard } from "@radarly/models/cards/model";
import { Clause } from "@radarly/models/clause";
import { hardMergeClauses } from "@radarly/models/clause/merge";
import { PostLensFactory } from "@radarly/models/posts";
import Tile from "@radarly/shared/cards/tile";
import PinnedPost from "@radarly/shared/widgets/pinned-post";
import { CardProps } from "../model";

export const getPinnedPostClause = (
  postId: string,
  baseClause?: Clause
): Clause => {
  if (!postId) {
    return null;
  }

  const postClause = {
    and: [{ query: `uid:"${postId}"` }],
  };
  if (baseClause) {
    return hardMergeClauses(baseClause, postClause);
  } else {
    return postClause;
  }
};

const PopoverContent = styled(SafeHTML)`
  color: ${content};
`;

const WrapperScopeActions = styled.div<{ selected: boolean }>`
  visibility: ${(props) => (props.selected ? "visible !important" : "hidden")};
  display: flex;
  align-items: center;
  color: ${(props) => content(props, "secondary")};
`;

const MetricsPopover = ({ post, locale, onOpenChange }) => {
  const { engagement, reach } = PostLensFactory.create(post).getMetrics();

  return (
    <PopoverButton icon="chart-up" onOpenChange={onOpenChange} side="top">
      <PopoverContent
        html={tr(locale, "CUSTOM_PAGES.WIDGET.METRICS_INFO", {
          reach: shorten(locale, reach),
          engagement: shorten(locale, engagement),
        })}
      />
    </PopoverButton>
  );
};

const RadarlyPinnedPostCard = ({
  clause,
  card,
  height,
  timezone,
  readOnly,
}: CardProps<PinnedPostCard>) => {
  const project = useCurrentProject();
  const locale = useCurrentUserLocale();
  const postId = card.dataviz.postId;
  const [isNarrow, setIsNarrow] = useState(false);

  const fragment =
    postId !== "" && card.fragments.length > 0 ? card.fragments[0] : null;

  const { post, loading, isError } = usePinnedPostWidget(project.id, fragment);
  const lens = post ? PostLensFactory.create(post) : null;
  const permalink = lens?.getPermalink();
  const [open, setOpen] = useState(false);

  const scopeActions =
    isNarrow && post ? (
      <WrapperScopeActions className="wrapper-scope-actions" selected={open}>
        <MetricsPopover onOpenChange={setOpen} post={post} locale={locale} />
        {permalink && (
          <Button
            icon="square-arrow"
            variant="tertiary"
            size="xs"
            onClick={() => window.open(permalink)}
          />
        )}
      </WrapperScopeActions>
    ) : (
      <div />
    );

  return (
    <Tile title={card.title} height={height} actions={scopeActions}>
      <PinnedPost
        project={project}
        post={post}
        setIsNarrow={setIsNarrow}
        loading={loading}
        isError={isError}
        locale={locale}
        timezone={timezone}
        readOnly={readOnly}
      />
    </Tile>
  );
};

export default RadarlyPinnedPostCard;
